import React, { useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'primereact/tag';
import AgendaHeader from './AgendaHeader';
import AgendaStatusBanner from './AgendaStatusBanner';
import AttentionPanel from './AttentionPanel';
import DailyAgendaView from './DailyAgendaView';
import WeeklyAgendaView from './WeeklyAgendaView';
import WeeklyTeamSummary from './WeeklyTeamSummary';
import { formatWeekRange } from '../utils/agendaDates';

const STATUS_OPTIONS = [
    { label: 'Todos los estados', value: '' },
    { label: 'Planificada', value: 'planned' },
    { label: 'En progreso', value: 'inProgress' },
    { label: 'Completada', value: 'completed' },
    { label: 'No completada', value: 'notCompleted' },
    { label: 'Pendiente de reporte', value: 'pendingReport' },
    { label: 'Cancelada', value: 'cancelled' }
];

const ORIGIN_OPTIONS = [
    { label: 'Todos los tipos', value: '' },
    { label: 'Planificada', value: 'planned' },
    { label: 'Inesperada', value: 'unplanned' },
    { label: 'Tiempo de oficina', value: 'office' }
];

const operationalStatus = (visit) => {
    if (visit?.planningStatus === 'cancelled') return 'cancelled';
    if (visit?.executionStatus && visit.executionStatus !== 'notStarted') return visit.executionStatus;
    return 'planned';
};

const TeamAgendaView = ({
    agents,
    selectedAgentId,
    onAgentChange,
    agenda,
    visits,
    overview,
    loading,
    loadingOverview,
    viewMode,
    dateLabel,
    selectedDate,
    selectedWeekStart,
    selectedWeekEnd,
    isPast,
    isCurrent,
    isFuture,
    activeVisitCount,
    canShowCreateVisit,
    canCreateVisit,
    canEditVisits,
    savingVisit,
    submittingAgenda,
    canSubmitAgenda,
    onSubmitAgenda,
    onViewModeChange,
    onDateChange,
    onPrevious,
    onNext,
    onToday,
    onNewVisit,
    onNewOffice,
    onEditVisit,
    onCancelVisit,
    canViewReport,
    isReportLocked,
    onReportVisit,
    canReviewAgenda,
    onReviewAgenda,
    weekDays,
    deadlineLabel,
    settings
}) => {
    const [statusFilter, setStatusFilter] = useState('');
    const [originFilter, setOriginFilter] = useState('');
    const [clientFilter, setClientFilter] = useState('');
    const selectedAgent = agents.find((agent) => agent._id === selectedAgentId) || null;
    const getAgentStatusLabel = (agent) => {
        if (!agent) return '';
        if (agent.sellerStatusLabel) return agent.sellerStatusLabel;
        if (!agent.active && agent.role?.uid !== 'seller') return 'Inactivo · Ya no es vendedor';
        if (!agent.active) return 'Inactivo';
        if (agent.role?.uid !== 'seller') return 'Ya no es vendedor';
        return 'Activo';
    };
    const getAgentStatusSeverity = (agent) => (agent?.sellerStatus === 'activeSeller' || (agent?.active && agent?.role?.uid === 'seller') ? 'success' : 'warning');
    const filteredVisits = useMemo(() => {
        const search = clientFilter.trim().toLocaleLowerCase('es');
        return visits.filter((visit) => {
            if (statusFilter && operationalStatus(visit) !== statusFilter) return false;
            if (originFilter === 'office' && visit?.activityType !== 'office') return false;
            if (
                originFilter &&
                originFilter !== 'office' &&
                (
                    visit?.activityType === 'office' ||
                    (visit?.visitOrigin || 'planned') !== originFilter
                )
            ) return false;
            if (search) {
                const name = `${visit?.clientName || ''} ${visit?.prospectName || ''} ${visit?.areaName || ''}`.toLocaleLowerCase('es');
                if (!name.includes(search)) return false;
            }
            return true;
        });
    }, [clientFilter, originFilter, statusFilter, visits]);

    const agentOptionTemplate = (agent) => agent ? (
        <div className="flex align-items-center justify-content-between gap-2 w-full">
            <div className="flex flex-column">
                <span>{agent.name || agent.email}</span>
                {agent.email ? <small className="text-color-secondary">{agent.email}</small> : null}
            </div>
            <Tag value={getAgentStatusLabel(agent)} severity={getAgentStatusSeverity(agent)} />
        </div>
    ) : null;

    return (
        <div className="flex flex-column gap-3">
            {loadingOverview ? (
                <div className="surface-card border-round p-4 shadow-1 text-center">
                    <ProgressSpinner style={{ width: '2.5rem', height: '2.5rem' }} />
                    <div className="text-color-secondary mt-2">Actualizando resumen semanal...</div>
                </div>
            ) : (
                <>
                    <WeeklyTeamSummary overview={overview} />
                    <AttentionPanel alerts={overview?.alerts} />
                </>
            )}

            <div className="surface-card border-round p-3 shadow-1 ventas-team-toolbar">
                <div className="grid p-fluid align-items-end">
                    <div className="col-12 md:col-7 lg:col-5">
                        <label className="block font-medium mb-2">Vendedor</label>
                        <Dropdown
                            value={selectedAgentId}
                            options={agents}
                            optionLabel="name"
                            optionValue="_id"
                            itemTemplate={agentOptionTemplate}
                            valueTemplate={agentOptionTemplate}
                            onChange={(event) => onAgentChange(event.value)}
                            placeholder="Selecciona un vendedor"
                            filter
                            className="w-full"
                        />
                    </div>
                    <div className="col-12 md:col-5 lg:col-4">
                        <div className="ventas-team-period">
                            <span className="text-color-secondary text-sm">Semana</span>
                            <strong>{formatWeekRange(selectedDate, settings)}</strong>
                        </div>
                    </div>
                    {canShowCreateVisit ? (
                        <div className="col-12 lg:col-3 flex flex-column gap-2 lg:align-items-end">
                            <Button
                                type="button"
                                label="Nueva visita"
                                icon="pi pi-plus"
                                outlined
                                className="w-full lg:w-auto"
                                onClick={onNewVisit}
                                disabled={!canCreateVisit || savingVisit || !selectedAgentId}
                                loading={savingVisit}
                            />
                            <Button
                                type="button"
                                label="Tiempo de oficina"
                                icon="pi pi-briefcase"
                                outlined
                                className="w-full lg:w-auto"
                                onClick={onNewOffice}
                                disabled={!canCreateVisit || savingVisit || !selectedAgentId}
                            />
                        </div>
                    ) : null}
                </div>
            </div>

            <div className="surface-card border-round p-3 shadow-1">
                <div className="grid p-fluid">
                    <div className="col-12 md:col-4">
                        <label className="block font-medium mb-2">Estado</label>
                        <Dropdown value={statusFilter} options={STATUS_OPTIONS} onChange={(event) => setStatusFilter(event.value)} />
                    </div>
                    <div className="col-12 md:col-4">
                        <label className="block font-medium mb-2">Tipo de actividad</label>
                        <Dropdown value={originFilter} options={ORIGIN_OPTIONS} onChange={(event) => setOriginFilter(event.value)} />
                    </div>
                    <div className="col-12 md:col-4">
                        <label className="block font-medium mb-2">Cliente, prospecto o zona</label>
                        <span className="p-input-icon-left w-full">
                            <i className="pi pi-search" />
                            <InputText value={clientFilter} onChange={(event) => setClientFilter(event.target.value)} placeholder="Buscar por nombre" className="w-full" />
                        </span>
                    </div>
                </div>
                <div className="text-sm text-color-secondary">{filteredVisits.length} actividades coinciden con los filtros.</div>
            </div>

            {!selectedAgentId ? (
                <div className="surface-card border-round p-4 shadow-1 text-center text-color-secondary">
                    Selecciona un vendedor para consultar su agenda.
                </div>
            ) : (
                <>
                    <AgendaStatusBanner
                        agenda={agenda}
                        activeVisitCount={activeVisitCount}
                        selectedWeekStart={selectedWeekStart}
                        selectedWeekEnd={selectedWeekEnd}
                        isPast={isPast}
                        isCurrent={isCurrent}
                        isFuture={isFuture}
                        isAdmin={canShowCreateVisit}
                        canSubmit={canSubmitAgenda}
                        submitting={submittingAgenda}
                        onSubmit={onSubmitAgenda}
                        deadlineLabel={deadlineLabel}
                    />
                    {canReviewAgenda ? (
                        <div className="flex justify-content-end">
                            <Button type="button" label="Revisar agenda enviada" icon="pi pi-verified" onClick={onReviewAgenda} />
                        </div>
                    ) : null}
                    {isPast ? (
                        <div className="surface-100 border-round p-3 text-color-secondary">
                            Semana pasada · Solo lectura, salvo acciones administrativas justificadas
                        </div>
                    ) : null}

                    <AgendaHeader
                        idPrefix="ventas-agenda-equipo"
                        title="Agenda del vendedor"
                        description="Consulta o gestiona la semana seleccionada."
                        viewMode={viewMode}
                        dateLabel={dateLabel}
                        selectedDate={selectedDate}
                        newVisitDisabled
                        onViewModeChange={onViewModeChange}
                        onDateChange={onDateChange}
                        onPrevious={onPrevious}
                        onNext={onNext}
                        onToday={onToday}
                        onNewVisit={onNewVisit}
                        hideNewVisitButton
                    />

                    {loading ? (
                        <div className="surface-card border-round p-4 shadow-1 text-center">
                            <ProgressSpinner style={{ width: '2.5rem', height: '2.5rem' }} />
                            <div className="text-color-secondary mt-2">Cargando agenda del equipo...</div>
                        </div>
                    ) : viewMode === 'day' ? (
                        <DailyAgendaView selectedDate={selectedDate} visits={filteredVisits} canEditVisits={canEditVisits} canViewReport={canViewReport} isReportLocked={isReportLocked} onEditVisit={onEditVisit} onCancelVisit={onCancelVisit} onReportVisit={onReportVisit} />
                    ) : (
                        <WeeklyAgendaView selectedDate={selectedDate} weekDays={weekDays} visits={filteredVisits} canEditVisits={canEditVisits} canViewReport={canViewReport} isReportLocked={isReportLocked} onEditVisit={onEditVisit} onCancelVisit={onCancelVisit} onReportVisit={onReportVisit} />
                    )}

                    {selectedAgent ? (
                        <div className="flex flex-column sm:flex-row sm:align-items-center gap-2 text-color-secondary text-sm">
                            <span>Mostrando agenda de {selectedAgent.name || 'vendedor seleccionado'}.</span>
                            <Tag value={getAgentStatusLabel(selectedAgent)} severity={getAgentStatusSeverity(selectedAgent)} />
                        </div>
                    ) : null}
                </>
            )}
        </div>
    );
};

export default TeamAgendaView;
